import { useState, useEffect } from 'react';
import { Crown, CreditCard, Search, Hash, Shield, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import ProToolLock from '@/components/ProToolLock';
import BinGenerator from '@/components/pro-tools/BinGenerator';
import CardValidator from '@/components/pro-tools/CardValidator';
import BinLookup from '@/components/pro-tools/BinLookup';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface Profile {
  is_premium: boolean | null;
  premium_tier: string | null;
}

const ProTools = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('profiles')
      .select('is_premium, premium_tier')
      .eq('id', user.id)
      .maybeSingle();
    if (data) {
      setProfile(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const isPremium = profile?.is_premium || false;
  const userTier = profile?.premium_tier || null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20">
          <Crown className="h-6 w-6 text-yellow-500" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">Pro Tools</h1>
            <Badge className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white border-0">
              PRO
            </Badge>
          </div>
          <p className="text-muted-foreground">
            Exclusive tools for TAM Pro & Elite members
          </p>
        </div>
      </div>

      <ProToolLock isPremium={isPremium} requiredTier="pro" userTier={userTier}>
        <Tabs defaultValue="bin-generator" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="bin-generator" className="gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">BIN Generator</span>
              <span className="sm:hidden">Generator</span>
            </TabsTrigger>
            <TabsTrigger value="card-validator" className="gap-2">
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Card Validator</span>
              <span className="sm:hidden">Validator</span>
            </TabsTrigger>
            <TabsTrigger value="bin-lookup" className="gap-2">
              <Search className="h-4 w-4" />
              <span className="hidden sm:inline">BIN Lookup</span>
              <span className="sm:hidden">Lookup</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bin-generator">
            <BinGenerator />
          </TabsContent>

          <TabsContent value="card-validator">
            <CardValidator />
          </TabsContent>

          <TabsContent value="bin-lookup">
            <BinLookup />
          </TabsContent>
        </Tabs>
      </ProToolLock>
    </div>
  );
};

export default ProTools;
